import { UNIT_SQM } from '@/constants'
import _ from 'lodash'
import moment from 'moment'

class CloudBasket {

  /** @type {String} */
  id = ""
  /** @type {Float} */
  width = 0.0
  /** @type {Float} */
  depth = 0.0
  /** @type {Boolean} */
  net_value_only = false
  /** @type {String}  */
  exhibition_start_date = ''
  /** @type {Object} */
  late_delivery = {
    active_days_before_event: 0,
    surcharge_in_percent: 0
  }
  /** @type {Boolean} */
  late_delivery_active = false

  /** @type {Object|null} */
  price_level = null

  /** @type {Number} */
  default_vat_rate = 19

  early_bird_mode = false

  /** @type {Array} */
  print_items = []
  /** @type {Float} */
  total_articles = 0.0
  /** @type {Float} */
  total_late_delivery = 0.0
  /** @type {Float} */
  total_price_level = 0.0
  /** @type {Float} */
  total = 0.0
  /** @type {Float} */
  total_gross = 0.0

  print_configurations = []
  booth_configurations = []

  /**
   * 
   * @param {Object} data
   */
  constructor(data) {
    this._init(data)
  }

  _init(data) {
    if (Object.values.length === 0) {
      return
    }

    for (const key in data) {
      if (Object.hasOwnProperty.call(data, key)) {
        this[key] = data[key]
      }
    }

    this.updateTotals()
  }

  clear() {
    this.print_items = []
    this.total_articles = 0.0
    this.total_late_delivery = 0.0
    this.total_price_level = 0.0
    this.total = 0.0
    this.total_gross = 0.0

    this.print_configurations = []
    this.booth_configurations = []
  }

  addItem(article, parentId) {
    let printItemIndex = this.print_items.findIndex((printItem) => printItem.parent_id === parentId)

    if (printItemIndex === -1) {
      article.parent_id = parentId
      this.print_items.push(_.cloneDeep(article))
      printItemIndex = this.print_items.length - 1
    }

    if (!this.print_items[printItemIndex].hasOwnProperty('count')) {
      this.print_items[printItemIndex].count = 0
    }

    this.print_items[printItemIndex].count += 1
    this.updateTotals()
  }

  addItemForArticlePrint(article, count) {
    const clonedArticle = _.cloneDeep(article)
    clonedArticle.count = count

    this.print_items.push(clonedArticle)
    this.updateTotals()
  }

  increaseItemCountForArticlePrint(article, count) {
    const index = this.print_items.findIndex((item) => {
      return (
        item.article_id === article.article_id
        && item.parent_id === article.parent_id
        && item.article_print_id === article.article_print_id
      )
    })

    if (index > -1) {
      this.print_items[index].count += count
      this.updateTotals()
    }
  }

  decreaseItemCountForArticlePrint(article, count) {
    const index = this.print_items.findIndex((item) => {
      return (
        item.article_id === article.article_id
        && item.parent_id === article.parent_id
        && item.article_print_id === article.article_print_id
      )
    })

    if (index === -1) {
      return
    }

    this.print_items[index].count -= count
    if (this.print_items[index].count === 0) {
      this.print_items.splice(index, 1)
    }
    this.updateTotals()
  }

  removeItem(parentId) {
    const index = this.print_items.findIndex((printItem) => printItem.parent_id === parentId)

    if (index === -1) {
      return
    }

    this.print_items[index].count -= 1
    if (this.print_items[index].count <= 0) {
      this.print_items.splice(index, 1)
    }

    this.updateTotals()
  }

  addItemForBoothPrint(article) {
    article.parent_id = null
    article.article_id = article.id
    article.exhibition_prices = JSON.parse(article.pivot.prices)
    this.print_items.push(article)

    this.updateTotals()
  }

  removeItemForBoothPrint(boothRefinementId) {
    const index = this.print_items.findIndex((item) => item.booth_refinement_id === boothRefinementId)
    if (index === -1) {
      return
    }

    this.print_items.splice(index, 1)
    this.updateTotals()
  }

  hasArticlePrintItem(article) {
    const items = this.print_items
      .filter((item) => {
        return (
          item.article_id === article.article_id
          && item.parent_id === article.parent_id
          && item.article_print_id === article.article_print_id
        )
      })

    return items.length > 0
  }

  hasBoothRefinementItem(articleId, boothRefinementId) {
    const items = this.print_items
      .filter((item) => {
        return (
          item.article_id === articleId
          && item.booth_refinement_id === boothRefinementId
        )
      })

    return items.length > 0
  }

  addPrintConfiguration(configuration) {
    this.print_configurations.push(configuration)
  }

  removePrintConfiguration(configuration) {
    const index = this.print_configurations.findIndex((item) => {
      return (
        configuration.id === item.id
        && configuration.group === item.group
        && configuration.article_id === item.article_id
        && configuration.parent_id === item.parent_id
        && configuration.article_print_id === item.article_print_id
      )
    })

    if (index === -1) {
      console.error('configuration not found')
    }

    this.print_configurations.splice(index, 1)
  }

  addBoothConfiguration(configuration) {
    this.booth_configurations.push(configuration)
  }

  removeBoothConfiguration(configuration) {
    const index = this.booth_configurations.findIndex((item) => {
      return configuration.id === item.id
    })

    if (index === -1) {
      console.error('configuration not found')
    }

    this.booth_configurations.splice(index, 1)
  }

  updateTotals() {
    // this.late_delivery_active = this.isLateDeliverySurchargeActive()
    this.total_articles = this.calculateTotalArticles()
    // this.total_late_delivery = this.calculateTotalLateDelivery()
    this.total_price_level = this.calculateTotalPriceLevel()
    this.total = this.calculateTotal()
    this.total_gross = this.calculateTotalGross()
  }

  isLateDeliverySurchargeActive() {
    if (this.early_bird_mode) {
      return false
    }

    const now = moment()
    const start = moment(this.exhibition_start_date)
  
    return this.late_delivery.active_days_before_event > start.diff(now, 'days')
  }

  calculateTotalArticles() {
    return this.print_items.reduce((a, item) => {
      const amount = (parseFloat(item.count) - parseFloat((item.inclusive) ? item.inclusive : 0))

      const netPrice = (!this.early_bird_mode)
        ? parseFloat(item.exhibition_prices.price_net)
        : parseFloat(item.exhibition_prices.price_net * (1 + (parseFloat(this.late_delivery.surcharge_in_percent) / 100)))
  
      return (a + (amount * netPrice))
    }, 0)
  }

  calculateTotalLateDelivery() {
    if (this.late_delivery_active) {
      return this.total_articles * (parseFloat(this.late_delivery.surcharge_in_percent) / 100)
    }

    return 0.0
  }

  calculateTotal() {
    return this.total_articles + this.total_price_level
  }

  calculateTotalGross() {
    return (this.net_value_only)
      ? this.total
      : this.total * (1 + (this.default_vat_rate / 100))
  }

  getVatRate() {
    return (this.net_value_only) ? 0 : this.default_vat_rate
  }

  calculateTotalPriceLevel() {
    if (this.price_level === null) {
      return 0.0
    }

    let charge = this.total_articles * (parseFloat(this.price_level.percentage) / 100)
    if (this.price_level.type === 0) {
      charge *= -1
    }

    return charge
  }
}

export default CloudBasket;